import { createStyles, Grid, makeStyles, Theme } from '@material-ui/core';
import Drawer from '@material-ui/core/Drawer';
import classNames from 'classnames';
import React from 'react';
import ConfirmOnlyFooter, { ConfirmOnlyFooterDetails } from './ConfirmOnlyFooter';
import PersonnelHeader, { PersonnelHeaderDetails } from './PersonnelHeader';
import StepperHeader from './StepperHeader';
import StepWithConfirmButtonFooter, { StepWithConfirmButtonFooterDetails } from './StepWithConfirmButtonFooter';
import TitleAndBackHeader, { TitleAndBackHeaderDetails } from './TitleAndBackHeader';

//  See bottom of file for documentation
interface BaseTnSlideInModalProps {
  isOpen: boolean;
  isLoading: boolean;
  handleClose: () => void;
  children: React.ReactNode;

  // Misc optionals
  customContentClass?: string | string[];
  drawerPaperClass?: boolean;
}

interface PersonnelHeaderProps {
  headerType: 'personnel';
  personnelHeaderDetails: PersonnelHeaderDetails;
}

interface TitleOnlyAndBackHeaderProps {
  headerType: 'titleOnlyAndBack';
  titleAndBackHeaderDetails: TitleAndBackHeaderDetails;
}

interface CustomHeaderProps {
  headerType: 'custom';
  headerContent: React.ReactNode;
}

type HeaderProps = PersonnelHeaderProps | TitleOnlyAndBackHeaderProps | CustomHeaderProps;

interface BaseFooterProps {
  shouldEnableConfirmButton: boolean;
  confirmButtonTitle: string;
  handleConfirmPressed: () => void;
}

interface ConfirmOnlyFooterProps extends BaseFooterProps {
  footerType: 'confirmOnly';
  confirmOnlyFooterDetails: ConfirmOnlyFooterDetails;
}

interface StepWithConfirmButtonFooterProps extends BaseFooterProps {
  footerType: 'stepWithConfirmButton';
  stepWithConfirmButtonFooterDetails: StepWithConfirmButtonFooterDetails;
}

interface CustomFooterProps {
  footerType: 'custom';
  footerContent: React.ReactNode;
}

type FooterProps = ConfirmOnlyFooterProps | StepWithConfirmButtonFooterProps | CustomFooterProps;

export interface TnSlideInModalProps extends BaseTnSlideInModalProps {
  header: HeaderProps;
  footer: FooterProps;
}

const TnSlideInModal = ({
  isOpen,
  isLoading,
  handleClose,
  children,
  header,
  footer,
  customContentClass,
  drawerPaperClass,
}: TnSlideInModalProps) => {
  const classes = useStyles();

  const renderHeader = () => {
    switch (header.headerType) {
      case 'personnel':
        return (
          <PersonnelHeader
            handleClose={() => handleClose()}
            firstName={header.personnelHeaderDetails.firstName}
            lastName={header.personnelHeaderDetails.lastName}
            personTitle={header.personnelHeaderDetails?.personTitle ?? ''}
            avatarURLDetail={header.personnelHeaderDetails?.avatarURLDetail}
          />
        );
      case 'titleOnlyAndBack':
        return (
          <TitleAndBackHeader
            title={header.titleAndBackHeaderDetails?.title}
            handleClose={() =>
              header.titleAndBackHeaderDetails.closeAction
                ? header.titleAndBackHeaderDetails.closeAction()
                : handleClose()
            }
          />
        );
      case 'custom':
        return header.headerContent;
      default:
        return null;
    }
  };

  const renderSubHeader = () => {
    if (footer.footerType !== 'stepWithConfirmButton') {
      return;
    } else {
      return (
        <Grid className={classes.subHeaderContainer}>
          <StepperHeader
            steps={footer.stepWithConfirmButtonFooterDetails.steps}
            currentStep={footer.stepWithConfirmButtonFooterDetails.currentStep}
            onChange={(stepNumber) => footer.stepWithConfirmButtonFooterDetails.handleStepChange(stepNumber)}
          />
        </Grid>
      );
    }
  };

  const renderFooter = () => {
    switch (footer.footerType) {
      case 'custom':
        return footer.footerContent ? footer.footerContent : <div></div>;
      case 'confirmOnly':
        return (
          <ConfirmOnlyFooter
            isLoading={isLoading}
            shouldEnableConfirmButton={footer.shouldEnableConfirmButton}
            confirmButtonTitle={footer.confirmButtonTitle ?? ''}
            handleConfirmPressed={footer.handleConfirmPressed}
          />
        );
      case 'stepWithConfirmButton':
        if (footer.stepWithConfirmButtonFooterDetails && footer.stepWithConfirmButtonFooterDetails.steps.length > 0) {
          const currentStep = footer.stepWithConfirmButtonFooterDetails.currentStep;

          return (
            <StepWithConfirmButtonFooter
              isLoading={isLoading}
              shouldEnableConfirmButton={footer.shouldEnableConfirmButton}
              confirmButtonTitle={footer.confirmButtonTitle ?? ''}
              handleConfirmPressed={footer.handleConfirmPressed}
              currentStep={currentStep}
              steps={footer.stepWithConfirmButtonFooterDetails.steps}
              handleStepChange={(stepNumber) => footer.stepWithConfirmButtonFooterDetails.handleStepChange(stepNumber)}
              secondaryConfirmDetails={footer.stepWithConfirmButtonFooterDetails.secondaryConfirmDetails}
            />
          );
        } else {
          return <div></div>;
        }
      default:
        return <div></div>;
    }
  };

  return (
    <Drawer
      open={isOpen}
      onBackdropClick={handleClose}
      onEscapeKeyDown={handleClose}
      classes={{ paper: classNames(classes.drawerPaper, drawerPaperClass ? classes.shortlistDrawerPaper : '') }}
      anchor="right"
      style={{
        zIndex: 10006,
      }}
    >
      <Grid container className={classes.modalContainer}>
        {/* Header */}
        <Grid container className={classes.headerContainer}>
          {renderHeader()}
          {renderSubHeader()}
        </Grid>
        {/* Content */}
        <Grid className={classNames(classes.content, customContentClass)}>{children}</Grid>

        {/* Footer */}
        <Grid className={classes.footerContainer}>{renderFooter()}</Grid>
      </Grid>
    </Drawer>
  );
};

export default TnSlideInModal;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    drawerPaper: {
      overflow: 'hidden',
      width: '50%',
      [theme.breakpoints.down(1080)]: {
        width: '90%',
      },
    },

    modalContainer: {
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      height: '100%',
      borderRadius: 8,
    },
    headerContainer: {
      flex: 0,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    subHeaderContainer: {
      flex: 1,
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    content: {
      flex: 1,
      overflowY: 'auto',
      width: '100%',
      '&::-webkit-scrollbar': {
        width: '8px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.primary.main,
        borderRadius: '8px',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: theme.palette.action.hover,
        borderRadius: '8px',
      },
    },
    footerContainer: {
      flex: 0,
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    shortlistDrawerPaper: {
      width: '40% !important',
      [theme.breakpoints.down(1080)]: {
        width: '90% !important',
      },
    },
  }),
);

/*
***** EXAMPLE COMPONENTS *****
 
    ** Personnel && Confirm button **
        <TnSlideInModal
          isLoading={false}
          isOpen={isModalOpen}
          handleClose={handleCloseModal}
          header={{
            headerType: 'personnel',
            personnelHeaderDetails: personnelHeaderDetails,
          }}
          footer={{
            footerType: 'confirmOnly',
            confirmOnlyFooterDetails: confirmOnlyFooterDetails,
            shouldEnableConfirmButton: true,
            confirmButtonTitle: 'Confirm',
            handleConfirmPressed: handleCloseModal,
          }}
        >
          <div style={{ fontSize: 64, padding: 24 }}>CONTENT HERE</div>
        </TnSlideInModal>
    ** TitleOnlyAndBack, Subheader Stepper && Stepper buttons **
        <TnSlideInModal
          isLoading={false}
          isOpen={isModalOpen}
          handleClose={handleCloseModal}
          header={{
            headerType: 'titleOnlyAndBack',
            titleAndBackHeaderDetails: titleAndBackHeaderDetails,
          }}
          footer={{
            footerType: 'stepWithConfirmButton',
            stepWithConfirmButtonFooterDetails: stepWithConfirmButtonFooter,
            shouldEnableConfirmButton: true,
            confirmButtonTitle: 'Submit',
            handleConfirmPressed: handleCloseModal,
          }}
        >
          <div style={{ fontSize: 64, padding: 24 }}>CONTENT HERE</div>
        </TnSlideInModal>
***** EXAMPLE OBJECTS & STATE *****
    const [currentStep, setCurrentStep] = useState(0);    
    const testSteps: Step[] = [
        {
          title: 'STEP ONE TITLE',
          content: <div style={{ fontSize: 64 }}>STEP 1 CONTENT</div>,
        },
        {
          title: 'STEP TWO TITLE',
          content: <div style={{ fontSize: 64 }}>STEP 2 CONTENT</div>,
        },
        {
          title: 'STEP THREE TITLE',
          content: <div style={{ fontSize: 64 }}>STEP 3 CONTENT</div>,
        },
        {
          title: 'STEP  FOUR TITLE',
          content: <div style={{ fontSize: 64 }}>STEP 4 CONTENT</div>,
        },
      ];
      const handleStepChange = (step: number) => {
        setCurrentStep(step);
      };
      const avatarURLDetail: AvatarURLDetail = {
        avatarURL:
          'https://upload.wikimedia.org/wikipedia/commons/2/24/Jack_Be_Nimble_2_-_WW_Denslow_-_Project_Gutenberg_etext_18546.jpg',
        isCompleteURLPath: true,
      };
      const personnelHeaderDetails: PersonnelHeaderDetails = {
        firstName: 'Jack',
        lastName: 'Benimble',
        personTitle: 'Quick Fellow',
        avatarURLDetail: avatarURLDetail,
      };
      const titleAndBackHeaderDetails: TitleAndBackHeaderDetails = {
        title: 'Important Work',
      };
      const confirmOnlyFooterDetails: ConfirmOnlyFooterDetails = {};
      const secondaryConfirmDetails: SecondaryConfirmDetails = {
        shouldEnableSecondaryConfirmButton: false,
        secondardyConfirmTitle: 'Update and Close',
        handleSecondaryConfirmPressed: handleCloseModal,
      };
      const stepWithConfirmButtonFooter: StepWithConfirmButtonFooterDetails = {
        handleStepChange: (number) => handleStepChange(number),
        currentStep: currentStep,
        steps: testSteps,
        secondaryConfirmDetails: secondaryConfirmDetails,
      };
 */
